/**
 * Renders an announcement's blocks in order. Each raw block is narrowed by
 * `normalizeBlock`; unknown/malformed blocks return null and are skipped, so the
 * modal never crashes on a future block type. See specs/in-app-announcements/spec.md.
 */

import React from 'react';
import { Pressable, Text, View } from 'react-native';
import { X } from 'lucide-react-native';
import { CdnImage } from '../../../src/shared/components/CdnImage/CdnImage';
import { openExternalUrl } from '../../../src/shared/helpers/openExternalUrl.helper';
import { normalizeBlock } from '../../../src/announcements/helpers/announcementBlocks.helper';
import {
  ANNOUNCEMENT_SPACER_SIZE_PX,
  ANNOUNCEMENT_TEST_IDS,
} from '../../../src/announcements/constants/announcements.constants';
import type {
  AnnouncementBlock,
  AnnouncementInAppAction,
  RawAnnouncementBlock,
} from '../../../src/announcements/types/announcements.types';
import { getAnnouncementModalStyles } from './AnnouncementModal.styles';

type Styles = ReturnType<typeof getAnnouncementModalStyles>;

interface AnnouncementBlocksProps {
  blocks: RawAnnouncementBlock[];
  onDismiss: () => void;
  /** Invoked when a button carries a recognized `inAppAction`; absent on hosts that do not handle any. */
  onInAppAction?: (action: AnnouncementInAppAction) => void;
  styles: Styles;
}

function renderBlock(
  block: AnnouncementBlock,
  index: number,
  styles: Styles,
  onDismiss: () => void,
  onInAppAction?: (action: AnnouncementInAppAction) => void,
): React.ReactElement | null {
  const testID = ANNOUNCEMENT_TEST_IDS.block(block.type, index);

  switch (block.type) {
    case 'heading':
      return (
        <Text
          key={index}
          testID={testID}
          style={[styles.heading, block.align === 'center' ? styles.alignCenter : styles.alignLeft]}
        >
          {block.text}
        </Text>
      );
    case 'paragraph':
      return (
        <Text
          key={index}
          testID={testID}
          style={[styles.paragraph, block.align === 'center' ? styles.alignCenter : styles.alignLeft]}
        >
          {block.text}
        </Text>
      );
    case 'image':
      return (
        <View key={index} testID={testID} style={[styles.image, { aspectRatio: block.aspectRatio ?? 16 / 9 }]}>
          <CdnImage
            uri={block.url}
            contentFit={block.contentFit ?? 'cover'}
            style={{ width: '100%', height: '100%', borderRadius: 12 }}
          />
        </View>
      );
    case 'button': {
      const isSecondary = block.variant === 'secondary';
      const onPress = () => {
        // A recognized in-app action takes precedence over the base kind (AC-2.3a); the
        // base kind is what installed clients that predate this hint fall back to.
        if (block.action.inAppAction && onInAppAction) {
          onInAppAction(block.action.inAppAction);
        } else if (block.action.kind === 'url') {
          openExternalUrl(block.action.url);
        }
        onDismiss();
      };
      return (
        <Pressable
          key={index}
          testID={testID}
          onPress={onPress}
          accessibilityRole="button"
          accessibilityLabel={block.label}
          style={({ pressed }) => [
            styles.button,
            isSecondary ? styles.buttonSecondary : styles.buttonPrimary,
            pressed && styles.buttonPressed,
          ]}
        >
          <Text style={isSecondary ? styles.buttonLabelSecondary : styles.buttonLabelPrimary}>
            {block.label}
          </Text>
        </Pressable>
      );
    }
    case 'spacer':
      return <View key={index} testID={testID} style={{ height: ANNOUNCEMENT_SPACER_SIZE_PX[block.size ?? 'md'] }} />;
    default:
      return null;
  }
}

export function AnnouncementBlocks({
  blocks,
  onDismiss,
  onInAppAction,
  styles,
}: AnnouncementBlocksProps): React.ReactElement {
  return (
    <>
      {blocks.map((raw, index) => {
        const normalized = normalizeBlock(raw);
        if (!normalized) return null; // unknown / malformed → skipped
        return renderBlock(normalized, index, styles, onDismiss, onInAppAction);
      })}
    </>
  );
}

/** Standalone close (X) control — always present so the modal is dismissible
 *  even when the message carries no button block. */
export function AnnouncementCloseButton({
  onDismiss,
  styles,
  a11yLabel,
}: {
  onDismiss: () => void;
  styles: Styles;
  a11yLabel: string;
}): React.ReactElement {
  return (
    <Pressable
      onPress={onDismiss}
      testID={ANNOUNCEMENT_TEST_IDS.CLOSE}
      accessibilityRole="button"
      accessibilityLabel={a11yLabel}
      style={({ pressed }) => [styles.closeButton, pressed && styles.closeButtonPressed]}
    >
      <X size={18} color="#E6E8EB" strokeWidth={2.5} />
    </Pressable>
  );
}
